// =================================================================================================
// >>> **CHECKOUT-SIGNIN-Q1: YES (hers, 2026-10-02) — THE TIER SHE PRESSED TRAVELS THROUGH SIGN-IN.** <<<
//
// Her words: after signing in from a tier button, go straight on to THAT tier's checkout. A signed-out
// Choose is refused by /api/stripe/checkout (401) and opens the sign-in door; "Continue with Google"
// is a full navigation that comes back to `/?auth=google-success`, so React remounts and anything
// held in memory (`pendingPage`) is gone. The carrier is `sessionStorage` for funnel-prefill's own
// recorded reason: it survives a refresh and the whole round trip to Google, and a new session starts
// clean, so a stranger's half-finished purchase never follows the next person on this browser.
//
// **ONE SHOT AND SHORT-LIVED.** The stash is taken (read AND removed) exactly once, and an entry older
// than 30 minutes is dropped rather than sending her to a checkout she has forgotten choosing.
//
// CLIENT-ONLY and pure: storage and clock are handed in, so a `.mjs` gate loads and drives it.
// =================================================================================================

export const PENDING_CHECKOUT_KEY = "th.pendingCheckout";
export const PENDING_CHECKOUT_TTL_MS = 30 * 60 * 1000;

type Store = { getItem(k: string): string | null; setItem(k: string, v: string): void; removeItem(k: string): void } | null | undefined;
export type CheckoutBody = { tier: string } | { product: string };

/** Only a tier or a product name, nothing else, ever goes into the stash. */
export function cleanCheckoutBody(body: unknown): CheckoutBody | null {
  const b = (body && typeof body === "object" ? body : {}) as Record<string, unknown>;
  const ok = (v: unknown) => typeof v === "string" && /^[a-z][a-z0-9-]{0,40}$/.test(v);
  if (ok(b.tier)) return { tier: String(b.tier) };
  if (ok(b.product)) return { product: String(b.product) };
  return null;
}

/** Remember the tier she pressed, signed out. A storage that throws remembers nothing. */
export function stashPendingCheckout(store: Store, body: unknown, now: number): boolean {
  const clean = cleanCheckoutBody(body);
  if (!store || !clean) return false;
  try { store.setItem(PENDING_CHECKOUT_KEY, JSON.stringify({ ...clean, at: now })); return true; } catch { return false; }
}

/** Take it, once: read and remove. Stale, malformed or missing answers null. */
export function takePendingCheckout(store: Store, now: number): CheckoutBody | null {
  if (!store) return null;
  let raw: string | null = null;
  try { raw = store.getItem(PENDING_CHECKOUT_KEY); store.removeItem(PENDING_CHECKOUT_KEY); } catch { return null; }
  if (!raw) return null;
  try {
    const v = JSON.parse(raw) as { at?: unknown };
    const at = Number(v?.at);
    if (!Number.isFinite(at) || now - at < 0 || now - at > PENDING_CHECKOUT_TTL_MS) return null;
    return cleanCheckoutBody(v);
  } catch { return null; }
}

/** Signing out forgets it: the next person on this tab is never carried to a checkout she did not press
 *  (the second reviewer's finding 1 on e90877a7). A storage that throws is simply left alone. */
export function clearPendingCheckout(store: Store): void {
  if (!store) return;
  try { store.removeItem(PENDING_CHECKOUT_KEY); } catch { /* nothing to clear */ }
}

type PayWindow = { location: { href: string }; history: { state: unknown; replaceState(s: unknown, t: string, u: string): void } } | null | undefined;
/** THE WIN-BACK'S BUTTON (her word, 2026-10-18: "go directly to pay"). `/membership?pay=vet` stashes that plan exactly as a
 *  signed-out Choose does and takes `pay` off the address, so a refresh or a shared link never starts it again. */
export function stashPayLink(win: PayWindow, store: Store, now: number): boolean {
  if (!win) return false;
  let url: URL;
  try { url = new URL(win.location.href); } catch { return false; }
  const tier = url.searchParams.get("pay");
  if (!tier) return false;
  url.searchParams.delete("pay");
  try { win.history.replaceState(win.history.state, "", `${url.pathname}${url.search}${url.hash}`); } catch { /* the stash still stands */ }
  return stashPendingCheckout(store, { tier }, now);
}
